import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layers, Loader2 } from 'lucide-react';
import { api, type DatalakeCoverage } from '../lib/api';
import { Card, CardHeader, CardTitle } from '../components/ui/Card';
import { formatRelative } from '../lib/format';
import { cn } from '../lib/utils';

export function DatalakeCoverage() {
  const coverage = useQuery({
    queryKey: ['datalake', 'coverage'],
    queryFn: () => api.getDatalakeCoverage(60),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Datalake · Coverage</h1>
        <p className="text-muted-foreground">
          Latest Glue partition date per client × Parquet table. Tells you whether each table is up
          to date for each client over the last 60 days.
        </p>
      </div>

      <CoverageCard
        coverage={coverage.data}
        loading={coverage.isLoading}
        error={coverage.error as Error | null}
      />
    </div>
  );
}

function CoverageCard({
  coverage,
  loading,
  error,
}: {
  coverage: DatalakeCoverage | undefined;
  loading: boolean;
  error: Error | null;
}) {
  const [filter, setFilter] = useState('');
  const filteredClients = useMemo(() => {
    if (!coverage) return [];
    if (!filter.trim()) return coverage.clients;
    const needle = filter.toLowerCase();
    return coverage.clients.filter((c) => c.toLowerCase().includes(needle));
  }, [coverage, filter]);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between pb-3 gap-3 flex-wrap">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Layers className="h-4 w-4 text-muted-foreground" />
            Glue coverage matrix
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {coverage && (
              <>
                Showing last {coverage.lookbackDays} days · checked{' '}
                {formatRelative(coverage.asOf)}.
              </>
            )}
          </p>
        </div>
        {coverage && (
          <div className="flex items-center gap-3">
            <Legend />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter clients…"
              className="h-9 w-44 rounded-md border border-border bg-background px-3 text-sm"
            />
          </div>
        )}
      </CardHeader>
      {error && <div className="px-6 pb-3 text-sm text-destructive">{error.message}</div>}
      {loading && (
        <div className="px-6 pb-6 text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Querying Glue partitions across all tables…
        </div>
      )}
      {coverage && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 border-y border-border">
              <tr>
                <th className="px-4 py-2 font-medium text-left text-muted-foreground sticky left-0 bg-muted/50">
                  Client
                </th>
                {coverage.tables.map((t) => (
                  <th
                    key={t}
                    className="px-3 py-2 font-medium text-left text-muted-foreground whitespace-nowrap"
                  >
                    {t}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr
                  key={client}
                  className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-2 font-medium sticky left-0 bg-card whitespace-nowrap">
                    {client}
                  </td>
                  {coverage.tables.map((t) => {
                    const date = coverage.cells[client]?.[t];
                    return <CoverageCell key={t} date={date} />;
                  })}
                </tr>
              ))}
              {filteredClients.length === 0 && coverage.clients.length > 0 && (
                <tr>
                  <td
                    colSpan={coverage.tables.length + 1}
                    className="px-4 py-6 text-center text-muted-foreground"
                  >
                    No clients match the filter.
                  </td>
                </tr>
              )}
              {coverage.clients.length === 0 && (
                <tr>
                  <td
                    colSpan={coverage.tables.length + 1}
                    className="px-4 py-6 text-center text-muted-foreground"
                  >
                    No partitions found in the lookback window.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function CoverageCell({ date }: { date: string | undefined }) {
  if (!date) {
    return <td className="px-3 py-2 text-muted-foreground">—</td>;
  }
  const ageDays = daysSince(date);
  const color =
    ageDays <= 1 ? 'text-success' : ageDays <= 3 ? 'text-warning' : 'text-destructive';
  return (
    <td
      className={cn('px-3 py-2 tabular-nums font-mono whitespace-nowrap', color)}
      title={`${ageDays}d ago`}
    >
      {date}
    </td>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
      <span className="inline-flex items-center gap-1">
        <span className="h-2 w-2 rounded-full bg-success" /> ≤1d
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="h-2 w-2 rounded-full bg-warning" /> 2–3d
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="h-2 w-2 rounded-full bg-destructive" /> &gt;3d
      </span>
    </div>
  );
}

function daysSince(yyyymmdd: string): number {
  const [y, m, d] = yyyymmdd.split('-').map((s) => parseInt(s, 10));
  if (!y || !m || !d) return Infinity;
  const target = Date.UTC(y, m - 1, d);
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.max(0, Math.floor((today - target) / 86400_000));
}
